/*
MIT LICENSE
Copyright (c) 2020-present Alibaba Cloud Design
Copied from antd/components/input/ClearableLabeledInput.tsx
Copyright (c) 2015-present Ant UED, https://xtech.antfin.com/

Permission is hereby granted, free of charge, to any person obtaining
a copy of this software and associated documentation files (the
"Software"), to deal in the Software without restriction, including
without limitation the rights to use, copy, modify, merge, publish,
distribute, sublicense, and/or sell copies of the Software, and to
permit persons to whom the Software is furnished to do so, subject to
the following conditions:

The above copyright notice and this permission notice shall be
included in all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND
NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE
LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION
WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE. */
import * as React from 'react';
import classNames from 'classnames';
import Icon from '../icon';
import { tuple } from '../_util/type';
import { cloneElement } from '../_util/reactNode';
import { getInputClassName, hasPrefixSuffix } from './utils';
const ClearableInputType = tuple('text', 'input');
function hasAddon(props) {
    return !!(props.addonBefore || props.addonAfter);
}
class ClearableLabeledInput extends React.Component {
    /** @private Do Not use out of this class. We do not promise this is always keep. */
    containerRef = React.createRef();
    onInputMouseUp = (e) => {
        if (this.containerRef.current?.contains(e.target)) {
            const { triggerFocus } = this.props;
            triggerFocus?.();
        }
    };
    renderClearIcon(prefixCls) {
        const { allowClear, value, disabled, readOnly, handleReset, suffix } = this.props;
        if (!allowClear) {
            return null;
        }
        const needClear = !disabled && !readOnly;
        const className = `${prefixCls}-clear-icon`;
        return (
        // B-Design Icon
        React.createElement(Icon, { type: 'close', onClick: handleReset, 
            // Do not trigger onBlur when clear input
            // https://github.com/ant-design/ant-design/issues/31200
            onMouseDown: (e) => e.preventDefault(), className: classNames({
                [`${className}-hidden`]: !needClear,
                [`${className}-has-suffix`]: !!suffix
            }, className), role: 'button' })
        // End of B-Design Icon
        );
    }
    renderSuffix(prefixCls) {
        const { suffix, allowClear } = this.props;
        if (suffix || allowClear) {
            return (React.createElement("span", { className: `${prefixCls}-suffix` },
                suffix,
                this.renderClearIcon(prefixCls)));
        }
        return null;
    }
    renderLabeledIcon(prefixCls, element) {
        const { focused, value, prefix, className, size, suffix, disabled, allowClear, direction, style, readOnly, bordered } = this.props;
        if (!hasPrefixSuffix(this.props)) {
            return cloneElement(element, {
                value
            });
        }
        const suffixNode = this.renderSuffix(prefixCls);
        const prefixNode = prefix ? React.createElement("span", { className: `${prefixCls}-prefix` }, prefix) : null;
        const affixWrapperCls = classNames(`${prefixCls}-affix-wrapper`, {
            [`${prefixCls}-affix-wrapper-focused`]: focused,
            [`${prefixCls}-affix-wrapper-disabled`]: disabled,
            [`${prefixCls}-affix-wrapper-xs`]: size === 'xs',
            [`${prefixCls}-affix-wrapper-sm`]: size === 'small',
            [`${prefixCls}-affix-wrapper-lg`]: size === 'large',
            [`${prefixCls}-affix-wrapper-input-with-clear-btn`]: suffix && allowClear && value,
            [`${prefixCls}-affix-wrapper-rtl`]: direction === 'rtl',
            [`${prefixCls}-affix-wrapper-readonly`]: readOnly,
            [`${prefixCls}-affix-wrapper-borderless`]: !bordered,
            [`${prefixCls}-success`]: this.props.state === 'success',
            [`${prefixCls}-loading`]: this.props.state === 'loading',
            [`${prefixCls}-error`]: this.props.state === 'error',
            [`${prefixCls}-warning`]: this.props.state === 'warning',
            [`${prefixCls}-gray`]: this.props.theme === 'gray',
            [`${prefixCls}-breathing`]: this.props.breathing,
            // className will go to addon wrapper
            [`${className}`]: !hasAddon(this.props) && className
        });
        return (React.createElement("span", { ref: this.containerRef, className: affixWrapperCls, style: style, onMouseUp: this.onInputMouseUp },
            prefixNode,
            cloneElement(element, {
                style: null,
                value,
                className: getInputClassName(prefixCls, bordered, size, disabled)
            }),
            suffixNode));
    }
    renderInputWithLabel(prefixCls, labeledElement) {
        const { addonBefore, addonAfter, style, size, className, direction, breathing } = this.props;
        // Not wrap when there is not addons
        if (!hasAddon(this.props)) {
            return labeledElement;
        }
        const wrapperClassName = `${prefixCls}-group`;
        const addonClassName = `${wrapperClassName}-addon`;
        const addonBeforeNode = addonBefore ? React.createElement("span", { className: addonClassName }, addonBefore) : null;
        const addonAfterNode = addonAfter ? React.createElement("span", { className: addonClassName }, addonAfter) : null;
        const mergedWrapperClassName = classNames(`${prefixCls}-wrapper`, wrapperClassName, {
            [`${wrapperClassName}-rtl`]: direction === 'rtl'
        });
        const mergedGroupClassName = classNames(`${prefixCls}-group-wrapper`, {
            [`${prefixCls}-group-wrapper-xs`]: size === 'xs',
            [`${prefixCls}-group-wrapper-sm`]: size === 'small',
            [`${prefixCls}-group-wrapper-lg`]: size === 'large',
            [`${prefixCls}-group-wrapper-rtl`]: direction === 'rtl',
            [`${prefixCls}-breathing`]: breathing
        }, className);
        // Need another wrapper for changing display:table to display:inline-block
        // and put style prop in wrapper
        return (React.createElement("span", { className: mergedGroupClassName, style: style },
            React.createElement("span", { className: mergedWrapperClassName },
                addonBeforeNode,
                cloneElement(labeledElement, { style: null }),
                addonAfterNode)));
    }
    renderTextAreaWithClearIcon(prefixCls, element) {
        const { value, allowClear, className, style, direction, bordered } = this.props;
        if (!allowClear) {
            return cloneElement(element, {
                value
            });
        }
        const affixWrapperCls = classNames(`${prefixCls}-affix-wrapper`, `${prefixCls}-affix-wrapper-textarea-with-clear-btn`, {
            [`${prefixCls}-affix-wrapper-rtl`]: direction === 'rtl',
            [`${prefixCls}-affix-wrapper-borderless`]: !bordered,
            [`${prefixCls}-success`]: this.props.state === 'success',
            [`${prefixCls}-loading`]: this.props.state === 'loading',
            [`${prefixCls}-warning`]: this.props.state === 'warning',
            // className will go to addon wrapper
            [`${className}`]: !hasAddon(this.props) && className
        });
        return (React.createElement("span", { className: affixWrapperCls, style: style },
            cloneElement(element, {
                style: null,
                value
            }),
            this.renderClearIcon(prefixCls)));
    }
    render() {
        const { prefixCls, inputType, element, breathing } = this.props;
        if (inputType === ClearableInputType[0]) {
            return this.renderTextAreaWithClearIcon(prefixCls, element);
        }
        return this.renderInputWithLabel(prefixCls, this.renderLabeledIcon(prefixCls, element));
    }
}
export default ClearableLabeledInput;
